<template>
  <div v-if="leftColumn || rightColumn" class="dual-column">
    <div class="dual-column__item" v-if="leftValidList.length">
      <div class="dual-column__atmosphere-layer" v-if="leftColumn?.logo">
        <img class="dual-column__atmosphere-bg" :src="leftColumn?.logo" alt="" />
      </div>
      <div class="dual-column__header">
        <span class="dual-column__title">{{ leftColumn?.title }}</span>
        <div
          class="dual-column__more-btn"
          @click="goToMerchantDetail(leftColumn?.contentPosition || '')"
          v-stat.click="{ tag: 'japan_dual_column.more_click', ext: { ftitle: leftColumn?.title } }"
        >
          <span>{{ $t('更多') }}</span>
        </div>
      </div>
      <div class="dual-column__list">
        <ThemeCard
          v-for="card in leftValidList"
          :key="card.title"
          :cardInfo="card"
          :cardType="ThemeCardType.row"
          :isShowCardDesc="false"
          class="dual-column__card"
        />
      </div>
    </div>
    <div class="dual-column__item" v-if="rightValidList.length">
      <div class="dual-column__atmosphere-layer" v-if="rightColumn?.logo">
        <img class="dual-column__atmosphere-bg" :src="rightColumn?.logo" alt="" />
      </div>
      <div class="dual-column__header">
        <span class="dual-column__title">{{ rightColumn?.title }}</span>
        <div
          class="dual-column__more-btn"
          @click="goToMerchantDetail(rightColumn?.contentPosition || '')"
          v-stat.click="{ tag: 'japan_dual_column.more_click', ext: { ftitle: rightColumn?.title } }"
        >
          <span>{{ $t('更多') }}</span>
        </div>
      </div>
      <div class="dual-column__list">
        <ThemeCard
          v-for="card in rightValidList"
          :key="card.title"
          :cardInfo="card"
          :cardType="ThemeCardType.row"
          :isShowCardDesc="false"
          class="dual-column__card"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useRouter } from 'vue-router/composables';
import ThemeCard from '@/components/common/theme/theme-card.vue';
import { ModuleName } from '@/types/module-type';
import { MerchantDpList, MerchantItem } from '@/types/merchant';
import { ThemeCardType } from '@/types';
import { useConfigStore } from '@/store';
import { JAPAN_DUAL_COLUMN_LEFT_INDEX, JAPAN_DUAL_COLUMN_RIGHT_INDEX } from '@/config/module-config/japan';

const props = defineProps<{ moduleName: ModuleName }>();
const emit = defineEmits(['onHideModule']);

const router = useRouter();
const { getConfig } = useConfigStore();

const leftColumn = ref<MerchantDpList | null>(null);
const rightColumn = ref<MerchantDpList | null>(null);

const filterValidMerchants = (list: MerchantItem[] = []) => list.filter(item => item.title).slice(0, 3);
const leftValidList = computed(() => filterValidMerchants(leftColumn.value?.merchantList));
const rightValidList = computed(() => filterValidMerchants(rightColumn.value?.merchantList));

function goToMerchantDetail(contentPosition: string) {
  router.push({ name: 'merchantDetail', query: { id: contentPosition } });
}

onBeforeMount(async () => {
  const result = await getConfig(props.moduleName);
  const allList: MerchantDpList[] = result?.merchantDpList || [];
  const left = allList[JAPAN_DUAL_COLUMN_LEFT_INDEX];
  const right = allList[JAPAN_DUAL_COLUMN_RIGHT_INDEX];

  if (left?.merchantList?.length) {
    leftColumn.value = left;
  }
  if (right?.merchantList?.length) {
    rightColumn.value = right;
  }

  if (!leftColumn.value && !rightColumn.value) {
    emit('onHideModule', props.moduleName);
  }
});
</script>

<style lang="less" scoped>
@import (reference) '~@/less/main';
@import (reference) '~@tencent/wphk-design-theme/index.less';
.dual-column {
  display: flex;
  flex-wrap: wrap;
  gap: @space-space-030;

  &__item {
    width: 335 / @rem;
    background: linear-gradient(180deg, #DDFFFB 0%, rgba(255, 255, 255, 0.8) 100%);
    border-radius: @radius-radius-040;
    overflow: hidden;
    position: relative;
    .dual-column__atmosphere-layer {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 176 / @rem;
      border-radius: @radius-radius-040;
      overflow: hidden;
      pointer-events: none;
      z-index: 0;
      .dual-column__atmosphere-bg {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top center;
      }
    }
    .dual-column__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: @space-space-050 @space-space-030 0 @space-space-040;
      position: relative;
      z-index: 1;
      .dual-column__title {
        .text-03-medium();
        color: #000;
      }
      .dual-column__more-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 @space-space-030;
        height: 40 / @rem;
        background: rgba(255, 255, 255, 0.8);
        border-radius: @radius-radius-050;
        .text-05-regular();
        color: #000;
        flex-shrink: 0;
        .actived();
      }
    }
    .dual-column__list {
      position: relative;
      z-index: 1;
      .dual-column__card {
        /deep/ .theme-row-card-wrapper {
          display: flex;
          align-items: center;
          padding: @space-space-030 0;
          margin: 0 @space-space-050;
          gap: @space-space-030;
          .logo {
            min-width: 64 / @rem;
            width: 64 / @rem;
            height: 64 / @rem;
            border-radius: @radius-radius-full;
            border: 1px solid @black-10-transparent;
            margin-right: 0;
          }
          .title {
            .text-04-regular();
            color: #000;
          }
        }
      }
    }
  }
}
</style>
